import { Button } from '@/components/ui/button'
import { HugeiconsIcon } from '@hugeicons/react'
import { RotateRight01Icon } from '@hugeicons/core-free-icons'
import React, { Component, ReactNode } from 'react'

interface ErrorBoundaryProps {
  children: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error?: Error
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="w-screen h-screen flex flex-col items-center justify-center p-4 gap-4">
          <h1 className="text-2xl font-bold">Oops, something went wrong.</h1>
          <p className="text-lg text-center max-w-md">
            Sorry for the inconvenience. If you don't mind helping, you can{' '}
            <a
              href="https://github.com/fiatjaf/pyramid/issues/new"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary underline"
            >
              submit an issue on GitHub
            </a>{' '}
            with the error details, or{' '}
            <a
              href="nostr:npub180cvv07tjdrrgpa0j7j7tmnyl2yr6yr7l8j4s3evf6u64th6gkwsyjh6w6"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary underline"
            >
              mention me
            </a>
            . Thank you for your support!
          </p>
          {this.state.error?.stack && (
            <>
              <Button
                onClick={() => {
                  navigator.clipboard.writeText(this.state.error!.stack!)
                }}
                variant="secondary"
              >
                Copy Error Stack
              </Button>
              <pre className="bg-destructive/10 text-destructive p-2 rounded text-wrap break-words whitespace-pre-wrap">
                {this.state.error.stack}
              </pre>
            </>
          )}
          <Button onClick={() => window.location.reload()} className="mt-2">
            <HugeiconsIcon icon={RotateRight01Icon} />
            Reload Page
          </Button>
          <div className="flex flex-wrap items-center justify-center gap-2">
            <Button
              variant="secondary"
              onClick={async () => {
                try {
                  const { resetStore } = await import('@/services/store.service')
                  await resetStore()
                } catch {
                  // ignore reset errors, reload anyway
                } finally {
                  window.location.reload()
                }
              }}
            >
              Clear Database
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                localStorage.clear()
                window.location.reload()
              }}
            >
              Clear localStorage
            </Button>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}
